import {
  PLAYBACK_FRAMES_PER_SECOND,
  SECOND_IN_MILLISECONDS,
  TEST,
} from '../../../constants';
import HTMLElementWrapper from './HTMLElementWrapper';
import { isNotNil } from '../../object';
import { log } from '../../log';
import MediaMetadataError from '../../../errors/MediaMetadataError';
import { hasValidVideoMetadata } from '../../mediaUtils';
import { clamp } from '../../numbers';
import CanvasElementWrapper from './CanvasElementWrapper';
import { deferTask } from '../../window';

const MAX_LOAD_ATTEMPTS = 5;

class VideoElementWrapper extends HTMLElementWrapper {
  constructor(url) {
    super('video');
    this.url = url;
    this.isLoadedAndBuffered = false;
  }

  loadAndBuffer = async (useCors = false, logAboutVideo = log) => {
    if (TEST || this.isLoadedAndBuffered) return true;

    let rejectError;
    const errorPromise = new Promise((resolve, reject) => {
      rejectError = reject;
    });
    this.element.onerror = () => {
      if (this.element.error) {
        const errorString = `Error ${this.element.error.code}; Details: ${this.element.error.message}`;
        logAboutVideo(errorString);
        rejectError(new Error(errorString));
      } else {
        rejectError(new Error('Could not load element'));
      }
    };

    const loadedMetadataPromise = this.loadMetadata();
    const loadedDataPromise = this.loadData();

    let isLoaded = false;
    const loadedPromise = Promise.all([
      loadedMetadataPromise,
      loadedDataPromise,
    ]).then(() => {
      isLoaded = true;
    });

    this.element.src = this.url;
    if (useCors) {
      this.element.crossOrigin = 'anonymous';
    }

    let attempts = 1;
    while (!isLoaded && attempts++ <= MAX_LOAD_ATTEMPTS + 1) {
      if (attempts > 0) logAboutVideo('Retrying...');

      this.element.load();
      const delayPromise = this.getDelayPromise(attempts);

      await Promise.race([delayPromise, loadedPromise, errorPromise]);

      if (!isLoaded) logAboutVideo("Didn't download in time.");
    }

    if (!isLoaded) return false;

    await this.buffer();

    delete this.element.onloadedmetadata;
    delete this.element.onloadeddata;
    delete this.element.oncanplaythrough;

    this.isLoadedAndBuffered = true;
    return true;
  };

  getScale(desiredWidth) {
    return clamp(desiredWidth / (this.element.videoWidth || 1), 0, 1.0);
  }

  /** @override */
  getDimensions() {
    return {
      height: this.element.videoHeight,
      width: this.element.videoWidth,
    };
  }

  goToFrame = async (frameIndex) => {
    if (isNotNil(this.goingToFramePromise)) {
      await this.goingToFramePromise;
    }

    let resolveSeeking;
    let resolveSeeked;
    let resolveTimeUpdate;

    this.frameNavigation = requestAnimationFrame(() => {
      this.element.onseeking = () => {
        resolveSeeking();
        delete this.element.onseeking;
      };
      this.element.onseeked = () => {
        resolveSeeked();
        delete this.element.onseeked;
      };
      this.element.ontimeupdate = () => {
        resolveTimeUpdate();
        delete this.element.ontimeupdate;
      };
      this.element.currentTime = frameIndex / PLAYBACK_FRAMES_PER_SECOND;
    });

    const seekingPromise = new Promise((resolve) => {
      resolveSeeking = resolve;
    });
    const seekedPromise = new Promise((resolve) => {
      resolveSeeked = resolve;
    });
    const timeUpdatePromise = new Promise((resolve) => {
      resolveTimeUpdate = resolve;
    });

    // FIXME (caskew): Seems like sometimes the video just uses the same frame over and over?
    this.goingToFramePromise = Promise.all([
      seekingPromise,
      seekedPromise,
      timeUpdatePromise,
    ]);
    await this.goingToFramePromise;

    return true;
  };

  loadMetadata() {
    return new Promise((resolve, reject) => {
      this.element.onloadedmetadata = () => {
        if (!hasValidVideoMetadata(this.element)) {
          reject(new MediaMetadataError(this.element));
        } else {
          resolve();
        }
      };
    });
  }

  loadData() {
    return new Promise((resolve) => {
      this.element.onloadeddata = resolve;
    });
  }

  getDelayPromise(attempts) {
    return new Promise((resolve) => {
      deferTask(resolve, 30 * SECOND_IN_MILLISECONDS * attempts);
    });
  }

  buffer() {
    return new Promise((resolve) => {
      this.element.oncanplaythrough = resolve;
    });
  }

  // TODO(caskew): Figure out where else to move this to, or just call it getDataURL?
  getCurrentFrameURL = (quality = undefined) => {
    const canvasWrapper = new CanvasElementWrapper();
    canvasWrapper.setPixels(this);
    return canvasWrapper.getDataURL(quality);
  };
}

export default VideoElementWrapper;
